<template>
  <section class="trendsreport-page">
    <div class="trendsreport-page__header">
      <div class="trendsreport-page__logo-container">
        <img
          src="/images/logos/trendsreport-logo.svg"
          alt="Trendsreport Logo"
          class="trendsreport-page__logo"
        />
        <img
          src="/images/logos/trendsreport-no-text-logo.svg"
          alt="Trendsreport Logo"
          class="trendsreport-page__logo mobile"
        />
        <div class="trendsreport-page__title"></div>
      </div>
      <p class="trendsreport-page__intro-text">
        Welcome to your subscription portal, {{ authUser.name }}!
      </p>
    </div>

    <div class="trendsreport-page__announcment-container">
      <TrendsReportBanner class="trendsreport-page_banner" />
      <div class="trendsreport-page__download-all" @click="downloadArticles()">
        <img
          src="/images/icons/icon-pdf.svg"
          alt="Adobe"
          class="trendsreport-page__adobe"
        />
        <p class="trendsreport-page__download-all-text">
          Download All Articles &#8594;
        </p>
      </div>
    </div>

    <div
      class="trendsreport-page__section-container my-aag"
      v-if="aagDashboard.length"
    >
      <h2
        class="trendsreport-page__section-title"
        id="my-at-a-glance-dashboard"
        ref="my-at-a-glance-dashboard"
      >
        My At-a-Glance Dashboard
      </h2>
    </div>
    <AtAGlanceDashboardTable
      v-if="aagDashboard.length"
      :cwaData="aagDashboard"
    />

    <div class="trendsreport-page__tutorials">
      <div
        class="trendsreport-page__favorites-header"
        v-if="!myFavoriteArticles.length"
      >
        <div class="trendsreport-page__tutorial-card">
          <div class="trendsreport-page__tutorial-content">
            <p class="trendsreport-page__tutorial-title">
              No Favorites Added Yet
            </p>
            <p class="trendsreport-page__tutorial-description">
              Customize your Trends Report experience by saving your favorite
              articles for easy viewing!
            </p>
          </div>
          <BaseButton
            class="trendsreport-page__tutorial-button favorites"
            @click="showFavoriteTutorial = !showFavoriteTutorial"
          >
            LEARN HOW
          </BaseButton>
        </div>
      </div>

      <BaseModal
        v-if="showFavoriteTutorial"
        color="grey"
        :even-padding="true"
        :rounded="true"
        :drop-shadow="true"
        @background-clicked="hideModal"
      >
        <section class="trendsreport-page__modal-content" ref="modalContent">
          <div class="trendsreport-page__modal-close" @click="hideModal">
            <span>X</span>
          </div>
          <div class="trendsreport-page__modal-body">
            <h1 class="trendsreport-page__modal-title">
              Save Your Favorite Articles
            </h1>
            <p class="trendsreport-page__modal-description">
              This video will provide a brief overview of how to save your
              favorite articles using the
              <strong>Star icon</strong>
              in onDemand. You will also learn how to use the
              <strong>Bell icon</strong>, which sets up notifications that will inform you whenever we
              update the pages for your favorites with new data and insights.
            </p>
            <div class="trendsreport-page__modal-video">
              <iframe
                src="https://player.vimeo.com/video/491791883"
                frameborder="0"
                webkitAllowFullScreen
                mozallowfullscreen
                allowFullScreen
                class="trendsreport-page__modal-vimeo-video"
                :height="height"
                :width="width"
              />
            </div>
          </div>
        </section>
      </BaseModal>

      <div class="trendsreport-page__aag-header" v-if="!aagDashboard.length">
        <div class="trendsreport-page__tutorial-card">
          <div class="trendsreport-page__tutorial-content">
            <p class="trendsreport-page__tutorial-title">
              Your customized At-a-Glance Dashboard will appear here!
            </p>
            <p class="trendsreport-page__tutorial-description">
              Get a quick overview of the economic trends most important to your
              business!
            </p>
          </div>
          <BaseButton
            class="trendsreport-page__tutorial-button aag"
            @click="showAAGTutorial = !showAAGTutorial"
          >
            LEARN HOW
          </BaseButton>
        </div>
      </div>

      <BaseModal
        v-if="showAAGTutorial"
        color="grey"
        :even-padding="true"
        :rounded="true"
        :drop-shadow="true"
        @background-clicked="hideModal"
      >
        <section class="trendsreport-page__modal-content" ref="modalContent">
          <div class="trendsreport-page__modal-close" @click="hideModal">
            <span>X</span>
          </div>
          <div class="trendsreport-page__modal-body">
            <h1 class="trendsreport-page__modal-title">
              Customize an At-a-Glance Dashboard
            </h1>
            <p class="trendsreport-page__modal-description">
              This video will provide a brief overview of the
              <strong>At-a-Glance dashboards</strong>
              and how to take the most relevant content from each to create your
              <strong>own personal dashboard</strong>
              in onDemand. By customizing your own At-a-Glance dashboard, you
              will be able to quickly access the most impactful insights to your
              business as soon as you log in. Create your own personalized
              experience!
            </p>
            <div class="trendsreport-page__modal-video">
              <iframe
                src="https://player.vimeo.com/video/491791883"
                frameborder="0"
                webkitAllowFullScreen
                mozallowfullscreen
                allowFullScreen
                class="trendsreport-page__modal-vimeo-video"
                :height="height"
                :width="width"
              />
            </div>
          </div>
        </section>
      </BaseModal>
    </div>

    <div class="trendsreport-page__filter-container">
      <div class="trendsreport-page__filter-input">
        <label class="trendsreport-page__filter-label">
          <IconMagnify
            color="grey"
            class="trendsreport-page__filter-icon"
            height="32"
            width="32"
          />
          <input
            v-model="searchTerm"
            type="text"
            class="trendsreport-page__input"
            placeholder="Search articles..."
          />
        </label>
      </div>
      <div class="trendsreport-page__filter-drowdown">
        <label class="trendsreport-page__filter-label-alt">
          <select
            name="businessPhase"
            v-model="businessPhase"
            @change="resetFilter"
            class="trendsreport-page__dropdown"
          >
            <option class="trendsreport-page__option" value="">
              Filter by Business Cycle
            </option>
            <!-- <option class="trendsreport-page__option" value=""></option> -->
            <option class="trendsreport-page__option" value="A">Phase A</option>
            <option class="trendsreport-page__option" value="B">Phase B</option>
            <option class="trendsreport-page__option" value="C">Phase C</option>
            <option class="trendsreport-page__option" value="D">Phase D</option>
          </select>
        </label>
      </div>
    </div>

    <div
      class="trendsreport-page__section-container"
      v-if="myFavoriteArticles.length"
    >
      <h2
        class="trendsreport-page__section-title"
        id="my-favorites"
        ref="my-favorites"
      >
        My Favorites
      </h2>
      <img
        src="/images/product/trendsreport/favorites.png"
        alt="Favorites Icon"
        class="trendsreport-page__section-icon-star"
      />
    </div>
    <TrendsReportCarousel
      v-if="myFavoriteArticles.length"
      list-name="favorite-article"
      :article-list="myFavoriteArticles"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="article"
      parent-slug="my-favorites"
      is-filtered
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="at-a-glance"
        ref="at-a-glance"
      >
        At-a-Glance Dashboards
      </h2>
      <img
        src="/images/product/trendsreport/at-a-glance.png"
        alt="At-a-glance Icon"
        class="trendsreport-page__section-icon-at-a-glance"
      />
    </div>
    <TrendsReportCarousel
      list-name="aag-article"
      :article-list="aagArticles"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="article"
      parent-slug="at-a-glance"
      is-filtered
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="core-articles"
        ref="core-articles"
      >
        Core Articles
      </h2>
      <img
        src="/images/product/trendsreport/core-articles.png"
        alt="Core Articles Icon"
        class="trendsreport-page__section-icon-core-articles"
      />
    </div>
    <TrendsReportCarousel
      list-name="core-article"
      :article-list="coreArticles"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="article"
      parent-slug="core-articles"
      is-filtered
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="financial-articles"
        ref="financial-articles"
      >
        Financial Articles
      </h2>
      <img
        src="/images/product/trendsreport/financial-articles.png"
        alt="Financial Articles Icon"
        class="trendsreport-page__section-icon-financial-articles"
      />
    </div>
    <TrendsReportCarousel
      list-name="financial-article"
      :article-list="financialArticles"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="article"
      parent-slug="financial-articles"
      is-filtered
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="manufacturing-articles"
        ref="manufacturing-articles"
      >
        Manufacturing Articles
      </h2>
      <img
        src="/images/product/trendsreport/manufacturing-articles.png"
        alt="Manufacturing Articles Icon"
        class="trendsreport-page__section-icon-manufacturing-articles"
      />
    </div>
    <TrendsReportCarousel
      list-name="manufacturing-article"
      :article-list="manufacturingArticles"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="article"
      parent-slug="manufacturing-articles"
      is-filtered
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="construction-articles"
        ref="construction-articles"
      >
        Construction Articles
      </h2>
      <img
        src="/images/product/trendsreport/construction-articles.png"
        alt="Construction Articles Icon"
        class="trendsreport-page__section-icon-construction-articles"
      />
    </div>
    <TrendsReportCarousel
      list-name="construction-article"
      :article-list="constructionArticles"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="article"
      parent-slug="construction-articles"
      is-filtered
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="weekly-qa"
        ref="weekly-qa"
      >
        Weekly Q&A Video
      </h2>
    </div>
    <TrendsReportCarousel
      list-name="qa"
      :article-list="monthlyQas"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      parent-slug="weekly-qa"
      item-type="qa"
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="trendstalk"
        ref="trendstalk"
      >
        TrendsTalk with ITR Economics
      </h2>
    </div>
    <TrendsReportCarousel
      list-name="trendstalk"
      :article-list="trendsTalks"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      parent-slug="trendstalk"
      item-type="trendstalk"
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="recent-announcements"
        ref="recent-announcements"
      >
        Recent Announcements
      </h2>
    </div>
    <TrendsReportCarousel
      list-name="recent-announcment"
      :article-list="recentAnnouncements"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="announcement"
      parent-slug="recent-announcements"
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="itr-academy"
        ref="itr-academy"
      >
        ITR Academy
      </h2>
    </div>
    <TrendsReportCarousel
      list-name="academy"
      :article-list="itrAcademy"
      :business-phase="businessPhase"
      :search-term="searchTerm"
      item-type="academy"
      parent-slug="itr-academy"
      @toggle-alert="handleAlert($event)"
      @toggle-favorite="handleFavorite($event)"
      @item-selected="handleItem"
    />

    <div class="trendsreport-page__section-container">
      <h2
        class="trendsreport-page__section-title"
        id="additional-resources"
        ref="additional-resources"
      >
        Additional Resources
      </h2>
    </div>
    <div class="trendsreport-page__additional-container">
      <AdditionalResourceComponent
        :resources="additionalResources"
        class="resources"
      />
      <div class="trendsreport-page__store-container">
        <div class="trendsreport-page__store-itr">
          <img
            src="/images/app-store-itr.png"
            alt="App Store ITR"
            class="trendsreport-page__store-image"
          />
          <p class="trendsreport-page__store-text">
            trends report is on the itr ondemand app!
          </p>
        </div>
        <div class="trendsreport-page__store-apps">
          <a
            href="https://play.google.com/store/apps/details?id=com.itrondemand.itrappdev&hl=en_US&gl=US"
            target="_blank"
          >
            <img
              src="/images/app-store-android-tr.png"
              alt="App Store Android"
              class="trendsreport-page__app-image"
            />
          </a>
          <a
            href="https://apps.apple.com/us/app/itr-ondemand/id1528665160"
            target="_blank"
          >
            <img
              src="/images/app-store-apple-tr.png"
              alt="App Store Apple"
              class="trendsreport-page__app-image last"
            />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import TrendsReportBanner from '~/components/trendsreport/TrendsReportBanner.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import AdditionalResourceComponent from '~/components/insider/AdditionalResourceComponent.vue';
  import TrendsReportCarousel from '~/components/trendsreport/TrendsReportCarousel.vue';
  import BaseModal from '~/components/base/BaseModal.vue';
  import IconMagnify from '~/components/icon/IconMagnify.vue';
  import AtAGlanceDashboardTable from '~/components/trendsreport/html/AtAGlanceComponents/AtAGlanceDashboardTable.vue';
  import { useAAGStore } from '~/stores/aag';

  export default {
    name: 'trendsreport-index-page',
    components: {
      TrendsReportBanner,
      BaseButton,
      AdditionalResourceComponent,
      TrendsReportCarousel,
      BaseModal,
      IconMagnify,
      AtAGlanceDashboardTable,
    },
    props: {
      authUser: {
        type: Object,
        required: true,
      },
      loadAssets: {
        type: Boolean,
        default: true,
      },
    },
    data: () => ({
      trgroups: [],
      coreArticles: [],
      financialArticles: [],
      manufacturingArticles: [],
      constructionArticles: [],
      monthlyQas: [],
      trendsTalks: [],
      recentAnnouncements: [],
      itrAcademy: [],
      additionalResources: [],
      aagArticles: [],
      myFavoriteArticles: [],
      showFavoriteTutorial: false,
      showAAGTutorial: false,
      height: 0,
      width: 0,
      searchTerm: '',
      businessPhase: '',
      ref: null,
    }),

    setup() {
      const aagStore = useAAGStore();
      return { aagStore };
    },

    async mounted() {
      if (window.location.href.includes('#')) {
        let urlArray = window.location.href.split('#');
        let ref = urlArray[urlArray.length - 1];
        this.ref = ref;
        const event = new CustomEvent('menu-clicked', {
          detail: {
            menuItem: `trendsreport-${this.ref}`,
          },
        });
        window.dispatchEvent(event);
      }
      await this.getMyAAGDashboard();
      await this.getMyFavorites();
      await this.getAagArticles();
      await this.getCoreArticles();
      await this.getFinancialArticles();
      await this.getManufacturingArticles();
      await this.getConstructionArticles();
      try {
        const monthlyQasResponse = await axios.get('/trendsreport/tr-qas');
        this.monthlyQas = monthlyQasResponse.data.data;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      try {
        const trendsTalksResponse = await axios.get(
          '/trendsreport/tr-trendstalks'
        );
        this.trendsTalks = trendsTalksResponse.data.data;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      try {
        const recentAnnouncementsResponse = await axios.get(
          '/trendsreport/tr-recent-announcements'
        );
        this.recentAnnouncements = recentAnnouncementsResponse.data.data;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      try {
        const itrAcademyResponse = await axios.get(
          '/trendsreport/tr-itr-academy'
        );
        this.itrAcademy = itrAcademyResponse.data.data;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      try {
        const additionalResourcesResponse = await axios.get(
          '/trendsreport/tr-additional-resources'
        );
        this.additionalResources = additionalResourcesResponse.data.data;
      } catch (err) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
      if (this.ref) {
        const elementByRef = this.$refs[this.ref];
        const rect = elementByRef.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        window.scrollTo({
          top: top,
          behavior: 'smooth',
        });
      }
    },
    methods: {
      async getMyAAGDashboard() {
        await this.aagStore.getAAGDashboard();
      },
      async downloadArticles() {
        axios({
          url: '/trendsreport/tr-download-latest-reports',
          method: 'GET',
          responseType: 'blob', // Indicate that the response data should be treated as a Blob
        })
          .then((response) => {
            const fileName = 'ITR Trends Report.pdf';
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          })
          .catch((error) => {
            // TODO: HANDLE ERROR - This is just silencing the error
          });
      },
      async getCoreArticles() {
        try {
          const coreArticlesResponse = await axios.get(
            '/trendsreport/tr-groups/core-articles'
          );
          this.coreArticles = coreArticlesResponse.data.data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getFinancialArticles() {
        try {
          const financialArticlesResponse = await axios.get(
            '/trendsreport/tr-groups/financial-articles'
          );
          this.financialArticles = financialArticlesResponse.data.data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getManufacturingArticles() {
        try {
          const manufacturingArticlesResponse = await axios.get(
            '/trendsreport/tr-groups/manufacturing-articles'
          );
          this.manufacturingArticles = manufacturingArticlesResponse.data.data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getConstructionArticles() {
        try {
          const constructionArticlesResponse = await axios.get(
            '/trendsreport/tr-groups/construction-articles'
          );
          this.constructionArticles = constructionArticlesResponse.data.data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getMyFavorites() {
        try {
          const myFavoritesResponse = await axios.get(
            '/trendsreport/tr-favorites'
          );
          this.myFavoriteArticles = myFavoritesResponse.data.data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async getAagArticles() {
        try {
          const aagArticlesResponse = await axios.get(
            '/trendsreport/tr-at-a-glance'
          );
          this.aagArticles = aagArticlesResponse.data.data;
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async handleAlert(article) {
        let body = {
          has_notification: article.notifications,
        };
        await axios.post(
          `/trendsreport/${article.id}/tr-toggle-notification`,
          body
        );
        this.refresh(article.parent_slug);
      },
      async handleFavorite(article) {
        let body = {
          is_favorite: article.favorite,
        };
        await axios.post(
          `/trendsreport/${article.id}/tr-toggle-favorite`,
          body
        );
        this.refresh(article.parent_slug);
      },
      async refresh(section) {
        this.getMyFavorites();
        this.getAagArticles();
        switch (section) {
          case 'core-articles':
            this.getCoreArticles();
            return;
          case 'financial-articles':
            this.getFinancialArticles();
            return;
          case 'manufacturing-articles':
            this.getManufacturingArticles();
            return;
          case 'construction-articles':
            this.getConstructionArticles();
            return;
          default:
            return;
        }
      },
      handleItem(item, parentSlug) {
        return (window.location.href = `/trendsreport/${parentSlug}/${item.slug}`);
      },
      hideModal() {
        document.body.classList.remove('modal-is-open');
        document.getElementById('site-header').classList.remove('hide-z-index');
        this.showAAGTutorial = false;
        this.showFavoriteTutorial = false;
      },
      resetFilter() {
        if (this.businessPhase === 'reset') {
          this.businessPhase = '';
        }
      },
    },

    watch: {
      showFavoriteTutorial(newValue) {
        if (newValue) {
          setTimeout(() => {
            const modalContentElement = this.$refs.modalContent;
            const width = modalContentElement.clientWidth;
            this.width = width;
            this.height = this.width * 0.54;
          }, 10);
        }
      },
      showAAGTutorial(newValue) {
        if (newValue) {
          setTimeout(() => {
            const modalContentElement = this.$refs.modalContent;
            const width = modalContentElement.clientWidth;
            this.width = width;
            this.height = this.width * 0.54;
          }, 10);
        }
      },
    },

    computed: {
      aagDashboard() {
        return this.aagStore.aagDashboardGetter;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .trendsreport-carousel-container {
    margin-bottom: 4rem;
    min-height: 150px;
  }

  .trendsreport-page {
    &__header {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 2rem;
      z-index: 0;
      align-items: center;
    }
    &__logo-container {
      display: flex;
      align-items: flex-end;
      gap: 1rem;
      // width: 25%;
      @include screen-sm {
        margin-right: 50px;
      }
    }
    &__logo {
      display: none;
      @include screen-sm {
        display: inline;
        width: 252px;
        height: 45px;
      }
      @include screen-md {
        width: 291px;
        height: 52px;
      }
      @include screen-lg {
        width: 308px;
        height: 55px;
      }
    }
    &__logo.mobile {
      display: inline;
      height: 48px;
      width: 48px;
      @include screen-sm {
        display: none;
      }
    }
    &__intro-text {
      font-size: 18px;
      font-weight: 700;
      margin: 0;
      color: var(--itr-blue);
      text-align: right;
      line-height: 24px;
      width: 75%;
      @include screen-sm {
        font-size: 20px;
      }
      @include screen-md {
        font-size: 25px;
        line-height: 2rem;
        padding-top: 14px;
      }
      @include screen-lg {
        font-size: 30px;
        padding-top: 0;
      }
    }
    &__announcment-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      margin: 0 8px 2rem 16px;
      @media (min-width: 1275px) {
        flex-direction: row;
      }
    }
    &__download-all {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      width: 100%;
      height: 65px;
      box-shadow: 4px 4px 4px 0px var(--itr-grey);
      border: 1px solid var(--itr-grey);
      border-radius: 8px;
      padding: 4px;
      cursor: pointer;
      @include screen-sm {
        width: 385px;
      }
      @include screen-xl {
        width: 361px;
        height: 155px;
        padding: 2px 20px 2px 20px;
        margin-left: 2rem;
      }
      @include screen-xxl {
        margin-left: 3rem;
      }
      &-text {
        font-size: 25px;
        font-weight: 700;
        color: var(--itr-blue);
        margin-left: 1rem;
        line-height: 2rem;
      }
    }
    &__adobe {
      height: 39px;
      width: 28px;
      @include screen-xl {
        height: 107px;
        width: 78px;
      }
    }
    &__tutorials {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-bottom: 3rem;
      @include screen-md {
        flex-direction: row;
      }
    }
    &__favorites-header {
      width: 100%;
      margin: 1rem 0;
      background:
        linear-gradient(white, white) padding-box,
        linear-gradient(to bottom, #09a684, #006742) border-box;

      border-radius: 10px;
      border: 3px solid transparent;
      @include screen-md {
        width: 47%;
        margin: 0;
      }
    }
    &__aag-header {
      width: 100%;
      margin: 1rem 0;
      background:
        linear-gradient(white, white) padding-box,
        linear-gradient(to bottom, #3075a4, #18395e) border-box;
      border-radius: 10px;
      border: 3px solid transparent;
      @include screen-md {
        width: 47%;
        margin: 0;
      }
    }
    &__tutorial-card {
      border-radius: 6px;
      padding: 12px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
      background: linear-gradient(95deg, #d9d9d9 12.68%, #f0f1f1 85.02%);
      @include screen-xl {
        flex-direction: row;
        gap: 1rem;
        padding: 32px 24px;
      }
    }
    &__tutorial-content {
      width: 100%;
      // @include screen-lg{
      //   width: 50%;
      // }
      @include screen-xl {
        width: 70%;
      }
    }
    &__tutorial-title {
      font-style: italic;
      font-weight: 700;
      color: var(--itr-grey-dark);
      font-size: 20px;
      @include screen-md {
        font-size: 25px;
      }
    }
    &__tutorial-description {
      color: var(--itr-grey-dark);
      font-size: 16px;
      margin-top: 12px;
      @include screen-md {
        font-size: 18px;
      }
      @include screen-lg {
        margin-left: 16px;
      }
    }
    &__tutorial-button {
      align-self: center;
      margin-top: 1rem;
      @include screen-xl {
        margin-top: 0;
      }
    }
    &__tutorial-button.aag {
      background: var(--itr-blue);
      &:hover {
        background: var(--itr-blue-mid);
      }
    }
    &__section-container {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin-bottom: 1rem;
    }
    &__section-container.my-aag {
      margin-bottom: 2rem;
    }
    &__section-title {
      margin: 0;
      font-size: 25px;
      font-weight: 700;
      @include screen-sm {
        font-size: 30px;
      }
      @include screen-lg {
        font-size: 35px;
      }
    }
    &__section-icon-star {
      margin-left: 12px;
      height: 26px;
      width: 30px;
      @include screen-sm {
        height: 36px;
        width: 40px;
      }
      @include screen-md {
        height: 46px;
        width: 50px;
      }
    }
    &__section-icon-at-a-glance,
    &__section-icon-financial-articles,
    &__section-icon-core-articles,
    &__section-icon-construction-articles {
      margin-left: 12px;
      height: 30px;
      width: 30px;
      @include screen-sm {
        height: 40px;
        width: 40px;
      }
      @include screen-md {
        height: 45px;
        width: 45px;
      }
    }
    &__section-icon-manufacturing-articles {
      margin-left: 12px;
      height: 30px;
      width: 40px;
      @include screen-sm {
        height: 40px;
        width: 54px;
      }
      @include screen-md {
        height: 45px;
        width: 60px;
      }
    }
    &__additional-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      gap: 2rem;
      justify-content: center;
      @include screen-xl {
        flex-direction: row;
        align-items: center;
      }
      .resources {
        flex-grow: 1;
      }
    }
    &__store-container {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      gap: 2rem;
    }
    &__store-itr {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      height: 100%;
      gap: 6px;
      @include screen-sm {
        gap: 20px;
      }
    }
    &__store-image {
      width: 68px;
      height: 68px;
      @include screen-sm {
        width: 90px;
        height: 90px;
      }
      @include screen-md {
        height: 107px;
        width: 107px;
      }
    }
    &__store-text {
      color: var(--itr-green-mid);
      max-width: 180px;
      font-weight: 600;
      font-size: 14px;
      text-transform: uppercase;
      text-align: center;
      line-height: 1.2;
    }
    &__store-apps {
      display: flex;
      flex-direction: column;
    }
    &__app-image {
      width: 200px;
      @include screen-sm {
        width: 229px;
      }
      @include screen-md {
        width: 275px;
      }
    }
    &__app-image.last {
      margin-top: -5px;
    }
    &__modal-close {
      color: var(--itr-white);
      font-size: 18px;
      font-weight: 700;
      height: 26px;
      width: 26px;
      border-radius: 50%;
      background: var(--itr-blue);
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 12px;
      right: 12px;
      cursor: pointer;
    }
    &__modal-title {
      text-align: center;
      margin-bottom: 1rem;
      @include screen-lg {
        font-size: 48px;
        font-weight: 700;
      }
    }
    &__modal-description {
      color: var(--itr-blue);
      margin-bottom: 2rem;
      font-size: 16px;
      @include screen-md {
        font-size: 20px;
      }
    }
    &__modal-video {
      margin: auto;
    }
    &__filter-container {
      margin-bottom: 1rem;
      display: flex;
      flex-direction: column;
      gap: 2rem;
      justify-content: center;
      align-items: center;
      @media (min-width: 636px) {
        flex-direction: row;
        justify-content: flex-start;
      }
    }
    &__filter-label {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      border-radius: 8px;
      border: 1px solid var(--itr-grey-mid);
      width: 283px;
      // height: 40px;
      @include screen-md {
        width: 287px;
      }
    }
    &__filter-label-alt {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      border-radius: 8px;
      width: 283px;
      @include screen-md {
        width: 287px;
      }
    }
    &__option-reset {
      visibility: hidden;
    }
    &__filter-icon {
      margin-left: 8px;
    }
    input,
    &__input {
      border: 0px !important;
      padding: 0px !important;
      margin-left: 12px;
      background: unset !important;
      color: var(--itr-grey-charcoal);
      height: 40px !important;
      min-height: 40px !important;
    }
    &__input::placeholder {
      font-style: italic;
      color: var(--itr-grey-mid);
    }
    select,
    &__select {
      border-radius: 8px !important;
      color: var(--itr-grey-charcoal);
      height: 43px !important;
      min-height: 43px !important;
      padding: 0 12px !important;
      background-position:
        calc(100% - 20px) calc(1em + 2px),
        calc(100% - 15px) calc(1em + 2px) !important;
    }
    select:focus {
      background-image:
        linear-gradient(45deg, transparent 50%, var(--itr-grey-mid) 50%),
        linear-gradient(135deg, var(--itr-grey-mid) 50%, transparent 50%) !important;
      border: 1px solid var(--itr-grey-mid);
    }
  }
</style>
